import { addDays, format, isAfter, parseISO, set } from 'date-fns';
import { toTenantDate } from '@utils/timeZone';
import { shortDate } from '@utils/i18n';
import { MAX_MULTI_DAYS } from '@forms/schedules/internal/constants';
import { BusyHit, BusyLookup, MultiDayState, ResourceKind } from './types';

export const dayKey = (date: Date) => format(date, 'yyyy-MM-dd');

export const isIncluded = (state: MultiDayState, key: string) => {
  const { start, end = start, weekdays, overrides } = state;
  if (!start || !end || key < start || key > end) return false;
  if (key in overrides) return overrides[key];
  return weekdays.includes(parseISO(key).getDay());
};

export const inRange = ({ start, end = start }: MultiDayState, key: string) =>
  Boolean(start && end && key >= start && key <= end);

export const getIncludedDays = (state: MultiDayState) => {
  if (!state.start) return [];
  const last = parseISO(state.end || state.start);
  const days: string[] = [];
  for (let day = parseISO(state.start); !isAfter(day, last); day = addDays(day, 1)) {
    const key = dayKey(day);
    if (isIncluded(state, key)) days.push(key);
    if (days.length >= MAX_MULTI_DAYS) break;
  }
  return days;
};

export const tapDay = (state: MultiDayState, key: string): MultiDayState => {
  const { start, end } = state;
  if (!start) return { ...state, start: key, end: undefined, overrides: {} };
  if (!end) return key < start ? { ...state, start: key } : { ...state, end: key };
  if (key >= start && key <= end)
    return { ...state, overrides: { ...state.overrides, [key]: !isIncluded(state, key) } };
  return key < start ? { ...state, start: key } : { ...state, end: key };
};

const window = (date: string, minutes: number) =>
  +set(parseISO(date), {
    hours: Math.floor(minutes / 60),
    minutes: minutes % 60,
    seconds: 0,
    milliseconds: 0,
  });

const toHits = ({ availability, days }: BusyLookup, kind: ResourceKind, id: string): BusyHit[] =>
  (availability?.[kind][id] || [])
    .map((scheduleId) => ({ scheduleId, item: availability?.schedules[scheduleId] }))
    .filter(({ item }) => item && days.has(item.date))
    .map(({ scheduleId, item }) => ({
      scheduleId,
      date: item!.date,
      startsAt: item!.startsAt,
      endsAt: item!.endsAt,
      taskName: item!.taskName,
      job: item!.projectIdentifier || item!.jobIdentifier,
      color: item!.color,
    }))
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));

export const getScheduled = (lookup: BusyLookup, kind: ResourceKind, id: string) =>
  toHits(lookup, kind, id);

export const getConflicts = (lookup: BusyLookup, kind: ResourceKind, id: string) =>
  toHits(lookup, kind, id).filter((hit) => {
    const start = +toTenantDate(hit.startsAt);
    const end = +toTenantDate(hit.endsAt);
    return start < window(hit.date, lookup.range.end) && window(hit.date, lookup.range.start) < end;
  });

export const formatDays = (days: string[]) => {
  if (!days.length) return '';
  const first = shortDate(parseISO(days[0]));
  return days.length > 1 ? `${first} – ${shortDate(parseISO(days[days.length - 1]))}` : first;
};
